/**
 * VideoCallWindow — janela da chamada de vídeo do atendente (VCALL-32..37, 44, 45; PRD US-7).
 *
 * Montada uma vez no shell: segue flutuando ao navegar pela plataforma (VCALL-33) e pode ser
 * ampliada. Mostra o estado da chamada (VCALL-34): chamando no app, sem resposta no app, link
 * enviado (canal e tipo), link gerado (copiar de novo), sala de espera (aprovar/recusar), em
 * andamento, reconectando e encerrada com o motivo. Também: modo de entrada escolhido (AC-7.6),
 * pergunta após 10 min sem ninguém entrar em chamadas APP/CHANNEL (VCALL-36), aviso de poucos
 * minutos (VCALL-45) e a oferta de voltar a uma chamada não terminal ao abrir a plataforma (VCALL-37).
 * No fim de uma chamada que aconteceu (encerrada, ou o atendente saiu de uma sala LINK que segue
 * aberta), mostra a pesquisa opcional de qualidade de áudio e vídeo.
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Maximize2, Minimize2, PictureInPicture2, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useVideoCall } from '@/contexts/VideoCallContext';
import {
    addVideoCallMoment, getVideoCallRecording, getVideoCallTranscript, getVideoCallTranscriberStatus, removeVideoCallMoment,
    requestVideoCallRecording, stopVideoCallRecording,
} from '@/services/videoCallApi';
import { cn } from '@/lib/utils';
import {
    useAdmitGuest, useEndVideoCall, useLeaveNoOneJoined, useRejectGuest,
} from '@/features/otto/api/useVideoCallApi';
import { VideoCallMedia, formatElapsed } from './VideoCallMedia';
import { CallQualitySurvey } from './CallQualitySurvey';
import { ConnectionQualityTracker } from './ConnectionQualityTracker';
import { createConnectionQualityTracker } from './connectionQualityStats';
import { useDraggableWindow } from './useDraggableWindow';
import { copyToClipboard } from './room/copyToClipboard';
import { DOCK_PANEL_HEIGHT } from './room/popoutWindow';
import { usePopoutWindow } from './room/usePopoutWindow';
import { useWaitingChime } from './room/waitingChime';
import { ENTRY_MODE_LABEL, channelTypeLabel, endReasonText } from './videoCallCopy';

/** O que a transcrição precisa da rede (o provedor da sala não conhece a camada de rede). */
const TRANSCRIPT_API = Object.freeze({
    getTranscript: getVideoCallTranscript,
    getTranscriberStatus: getVideoCallTranscriberStatus,
    addMoment: addVideoCallMoment,
    removeMoment: removeVideoCallMoment,
});

/** O que a gravação da chamada precisa da rede (o provedor da sala não conhece a camada de rede). */
const RECORDING_API = Object.freeze({
    getPanel: getVideoCallRecording,
    request: requestVideoCallRecording,
    stop: stopVideoCallRecording,
});

/**
 * Tamanho da janelinha da guia. Com a Jev ela é mais larga (a barra ganha o botão da transcrição e o marcador de
 * momento) e, com a transcrição aberta, mais alta: a gaveta tem altura fixa e o vídeo não encolhe.
 */
export function compactWindowClass(call, dockOpen) {
    return cn(
        'bottom-4 right-4 max-h-[calc(100vh-2rem)] max-w-[calc(100vw-2rem)]',
        call.transcriptionEnabled ? 'w-[420px]' : 'w-[360px]',
        dockOpen ? 'h-[720px]' : 'h-[420px]',
    );
}

/** 10 min sem ninguém entrar (VCALL-36). */
export const NO_ONE_JOINED_MS = 10 * 60 * 1000;

const TERMINAL = new Set(['ENDED', 'MISSED', 'DECLINED', 'APP_NO_ANSWER', 'FAILED']);

/** Atendente e contato chegaram a estar na sala (só então há áudio e vídeo para avaliar). */
export function everConnected(call) {
    return Boolean(call?.wasActive || call?.firstJoinedAt || call?.status === 'ACTIVE' || call?.status === 'IDLE');
}

/** A pesquisa de qualidade aparece na tela final desta chamada? */
export function qualitySurveyApplies(call) {
    return Boolean(call) && everConnected(call) && (call.status === 'ENDED' || Boolean(call.leftRoom));
}

/**
 * Título do estado atual (VCALL-34). `peerLeft`: o contato clicou em sair (não caiu): o servidor chama os dois de
 * `IDLE`, mas só quem caiu está "reconectando".
 */
function statusText(call, peerLeft = false) {
    if (TERMINAL.has(call.status)) return 'Chamada encerrada';
    if (call.leftRoom) return 'Você saiu da sala';
    if (call.status === 'RINGING_APP') return call.appStatus === 'RINGING' ? 'Tocando no app do contato…' : 'Chamando no app…';
    if (call.status === 'IDLE') return peerLeft ? 'O contato saiu da chamada' : 'Reconectando…';
    if (call.status === 'ACTIVE') return 'Em andamento';
    if (call.deliveryMethod === 'CHANNEL') return 'Link enviado';
    if (call.deliveryMethod === 'LINK') return 'Link gerado';
    return 'Aguardando o contato entrar';
}

function useElapsedSeconds(call) {
    const [activeSince, setActiveSince] = useState(null);
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (call?.status === 'ACTIVE' && !activeSince) {
            setActiveSince(call.firstJoinedAt ? new Date(call.firstJoinedAt).getTime() : Date.now());
        }
        if (!call) setActiveSince(null);
    }, [call, activeSince]);

    useEffect(() => {
        if (!activeSince) return undefined;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [activeSince]);

    return activeSince ? Math.max(0, Math.floor((now - activeSince) / 1000)) : 0;
}

/** Pergunta "sair ou continuar" quando ninguém entrou em 10 min numa chamada APP/CHANNEL. */
function useNoOneJoinedPrompt(call) {
    const [dueAt, setDueAt] = useState(null);
    const [dismissedFor, setDismissedFor] = useState(null);
    const [due, setDue] = useState(false);

    const applies = Boolean(call) && call.deliveryMethod !== 'LINK'
        && (call.status === 'WAITING' || call.status === 'RINGING_APP');

    useEffect(() => {
        if (!applies) { setDueAt(null); setDue(false); return undefined; }
        const created = call.createdAt ? new Date(call.createdAt).getTime() : Date.now();
        const at = created + NO_ONE_JOINED_MS;
        setDueAt(at);
        const wait = at - Date.now();
        if (wait <= 0) { setDue(true); return undefined; }
        setDue(false);
        const id = setTimeout(() => setDue(true), wait);
        return () => clearTimeout(id);
    }, [applies, call?.id, call?.createdAt]);

    return {
        show: applies && due && dueAt !== null && dismissedFor !== call?.id,
        dismiss: () => setDismissedFor(call?.id ?? null),
    };
}

function ResumePrompt({ call, onResume, onDismiss }) {
    return (
        <div role="dialog" aria-label="Chamada de vídeo em andamento" className="fixed bottom-4 right-4 z-[60] w-80 rounded-xl border border-border bg-surface p-4 shadow-xl">
            <p className="text-sm font-medium text-foreground">Você tem uma chamada de vídeo em andamento</p>
            {call.contactName && <p className="mt-1 text-xs text-foreground-muted">Com {call.contactName}</p>}
            <div className="mt-3 flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={onDismiss}>Agora não</Button>
                <Button size="sm" onClick={onResume}>Voltar à chamada</Button>
            </div>
        </div>
    );
}

function WaitingRoom({ guests, onAdmit, onReject, busy }) {
    if (!guests.length) return null;
    return (
        <section aria-label="Sala de espera" className="border-b border-border bg-surface-hover/50 px-3 py-2">
            <p className="mb-1 text-xs font-medium text-foreground">Sala de espera</p>
            <ul className="space-y-1">
                {guests.map((guest) => (
                    <li key={guest.participantId} className="flex items-center justify-between gap-2 text-sm">
                        <span className="truncate">{guest.displayName} quer entrar</span>
                        <span className="flex gap-1">
                            <Button size="sm" variant="ghost" disabled={busy} onClick={() => onReject(guest)}>Recusar</Button>
                            <Button size="sm" disabled={busy} onClick={() => onAdmit(guest)}>Aprovar</Button>
                        </span>
                    </li>
                ))}
            </ul>
        </section>
    );
}

export function VideoCallWindow() {
    const {
        call, connection, waitingGuests, minutesWarningSeconds, resumableCall,
        resumeCall, dismissResumable, clearCall, leaveRoom, finishCall, removeWaitingGuest,
    } = useVideoCall();
    // A chamada abre em visão cheia (a sala ampliada, com a transcrição ao lado); "Reduzir janela" a leva para a janelinha
    // do canto. Cada chamada nova volta à visão cheia.
    const [expanded, setExpanded] = useState(true);
    const endMutation = useEndVideoCall();
    const leaveMutation = useLeaveNoOneJoined();
    const admitMutation = useAdmitGuest();
    const rejectMutation = useRejectGuest();
    // Alguém novo pede para entrar: o banner e a aba Participantes avisam, e o som chama a atenção de quem está em outra aba
    useWaitingChime(waitingGuests);
    const elapsedSeconds = useElapsedSeconds(call);
    const noOneJoined = useNoOneJoinedPrompt(call);
    // Medição de conexão da chamada atual; sobrevive ao fim da sala para ir com a pesquisa e
    // recomeça do zero quando outra chamada abre a janela.
    const [qualityTracker] = useState(createConnectionQualityTracker);
    useEffect(() => { qualityTracker.reset(); }, [call?.id, qualityTracker]);
    // Se a transcrição está aberta (a escolha da pessoa; `null` é "ainda não escolheu", e as janelas compactas começam
    // com ela fechada). Mora aqui, e não no provedor, porque a janela precisa crescer para acomodar a gaveta.
    const [panelPref, setPanelPref] = useState(null);
    useEffect(() => { setPanelPref(null); }, [call?.id]);
    useEffect(() => { setExpanded(true); }, [call?.id]);
    const dockOpen = panelPref === true && Boolean(call?.transcriptionEnabled);
    // A sala pode ir para uma janela flutuante fora da guia; o botão fica no cabeçalho, ao lado de ampliar. A janela
    // flutuante cresce quando a gaveta da transcrição abre.
    const popout = usePopoutWindow({ extraHeight: dockOpen ? DOCK_PANEL_HEIGHT : 0 });
    // O contato clicou em sair (a sala avisa): o cabeçalho diz isso em vez de "Reconectando…".
    const [peerLeft, setPeerLeft] = useState(false);
    // Com a sala na janela flutuante a janelinha da chamada vira uma barra pequena que se arrasta para
    // onde não atrapalhe o atendimento (ver `minimized`).
    const windowRef = useRef(null);
    const dragWindow = useDraggableWindow(windowRef, { enabled: popout.isOpen });

    if (!call) {
        return resumableCall
            ? <ResumePrompt call={resumableCall} onResume={resumeCall} onDismiss={dismissResumable} />
            : null;
    }

    const terminal = TERMINAL.has(call.status);
    // Tela final: chamada terminal, ou o atendente saiu de uma sala LINK que segue aberta.
    const showEnd = terminal || Boolean(call.leftRoom);
    const isLink = call.deliveryMethod === 'LINK';
    // A chamada já começou: o tempo corre também com o outro lado reconectando (IDLE).
    const showTimer = (call.status === 'ACTIVE' || call.status === 'IDLE') && !showEnd;
    // A sala está na janela flutuante: aqui sobra só o cabeçalho (e os avisos que precisam de resposta, como
    // a sala de espera). A mídia segue montada, escondida, porque é ela que desenha a sala na outra janela.
    const minimized = popout.isOpen && !showEnd;

    const handleEnd = async () => {
        try {
            const result = await endMutation.mutateAsync(call.id);
            finishCall(result);
        } catch (err) {
            if (err?.code === 'VIDEO_CALL_ENDED') finishCall({ status: 'ENDED', endReason: null });
            else toast.error('Não foi possível encerrar a chamada');
        }
    };

    const handleLeaveNoOneJoined = async () => {
        try {
            const result = await leaveMutation.mutateAsync(call.id);
            finishCall(result);
        } catch {
            toast.error('Não foi possível sair da chamada');
        }
    };

    const decide = (mutation, guest) => mutation.mutateAsync({ videoCallId: call.id, participantId: guest.participantId })
        .then(() => removeWaitingGuest(guest.participantId))
        .catch(() => toast.error('Não foi possível registrar a decisão'));

    // `doc` é o documento do botão clicado: com a sala na janela flutuante não é o da guia, e só o
    // documento com o foco consegue escrever na área de transferência. Devolve se copiou (o botão confirma).
    const copyLink = async (doc) => {
        const copied = await copyToClipboard(call.joinUrl, doc);
        if (copied) toast.success('Link copiado');
        else toast.error('Não foi possível copiar o link');
        return copied;
    };

    return (
        <div
            ref={windowRef}
            role="dialog"
            aria-label="Chamada de vídeo"
            data-minimized={minimized ? 'true' : 'false'}
            style={minimized ? dragWindow.style : undefined}
            className={cn(
                'fixed z-[60] flex flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-2xl',
                minimized && 'bottom-4 right-4 w-[320px] max-w-[calc(100vw-2rem)]',
                // A tela final (chamada encerrada, pesquisa de qualidade) cabe na janelinha: não ocupa a tela toda.
                !minimized && (expanded && !showEnd ? 'inset-4' : compactWindowClass(call, dockOpen)),
            )}
        >
            <header
                {...(minimized ? dragWindow.handlers : {})}
                {...(minimized ? { role: 'group', tabIndex: 0, title: 'Arraste para mover', 'aria-label': 'Mover a janela da chamada (arraste ou use as setas)' } : {})}
                className={cn(
                    'flex items-center gap-2 border-b border-border px-3 py-2',
                    minimized && 'touch-none select-none rounded-t-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary',
                    minimized && (dragWindow.dragging ? 'cursor-grabbing' : 'cursor-grab'),
                )}
            >
                <Video className="h-4 w-4 shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{call.title || call.contactName || 'Chamada de vídeo'}</p>
                    <p className="truncate text-xs text-foreground-muted">
                        {statusText(call, peerLeft)}
                        {showTimer && (
                            <>
                                {' · '}
                                <span aria-label="Duração da chamada" className="font-mono tabular-nums">{formatElapsed(elapsedSeconds)}</span>
                            </>
                        )}
                        {call.deliveryMethod !== 'APP' && call.entryMode ? ` · Entrada: ${ENTRY_MODE_LABEL[call.entryMode]}` : ''}
                    </p>
                </div>
                {popout.supported && connection && !showEnd && (
                    <Button
                        size="icon"
                        variant="ghost"
                        className={cn('h-7 w-7', popout.isOpen && 'bg-primary/10 text-primary')}
                        aria-label={popout.isOpen ? 'Voltar para a guia' : 'Abrir em janela flutuante'}
                        aria-pressed={popout.isOpen}
                        title={popout.isOpen ? 'Voltar para a guia' : 'Abrir em janela flutuante (fora desta guia)'}
                        onClick={popout.toggle}
                    >
                        <PictureInPicture2 className="h-4 w-4" />
                    </Button>
                )}
                {!minimized && !showEnd && (
                    <Button size="icon" variant="ghost" className="h-7 w-7" aria-label={expanded ? 'Reduzir janela' : 'Ampliar janela'} onClick={() => setExpanded((v) => !v)}>
                        {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                    </Button>
                )}
            </header>

            {minutesWarningSeconds !== null && !showEnd && (
                <div role="status" className="bg-amber-500/15 px-3 py-1.5 text-xs text-amber-800 dark:text-amber-300">
                    {`Restam ${Math.max(1, Math.ceil(minutesWarningSeconds / 60))} min de vídeo neste mês`}
                </div>
            )}

            {noOneJoined.show && (
                <div role="alertdialog" aria-label="Ninguém entrou" className="border-b border-border bg-surface-hover/50 px-3 py-2 text-sm">
                    <p>Ninguém entrou em 10 minutos. Deseja sair ou continuar na sala?</p>
                    <div className="mt-2 flex justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={noOneJoined.dismiss}>Continuar na sala</Button>
                        <Button size="sm" variant="destructive" disabled={leaveMutation.isPending} onClick={handleLeaveNoOneJoined}>Sair</Button>
                    </div>
                </div>
            )}

            {!showEnd && (
                <WaitingRoom
                    guests={waitingGuests}
                    busy={admitMutation.isPending || rejectMutation.isPending}
                    onAdmit={(guest) => decide(admitMutation, guest)}
                    onReject={(guest) => decide(rejectMutation, guest)}
                />
            )}

            {!showEnd && call.status === 'WAITING' && call.deliveryMethod === 'CHANNEL' && (
                <p className="px-3 py-2 text-xs text-foreground-muted">
                    {`Link enviado por ${call.channelName ?? 'canal'}${call.channelType ? ` (${channelTypeLabel(call.channelType)})` : ''}`}
                </p>
            )}

            {showEnd ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 overflow-y-auto p-4 text-center">
                    <div>
                        <p className="text-base font-medium text-foreground">{terminal ? 'Chamada encerrada' : 'Você saiu da sala'}</p>
                        {terminal && call.endReason && <p className="text-sm text-foreground-muted">{endReasonText(call.endReason)}</p>}
                        {!terminal && <p className="text-sm text-foreground-muted">O link continua valendo até expirar</p>}
                    </div>
                    {qualitySurveyApplies(call) ? (
                        <CallQualitySurvey
                            key={call.id}
                            videoCallId={call.id}
                            getConnectionQuality={qualityTracker.snapshot}
                            onDone={clearCall}
                        />
                    ) : (
                        <Button size="sm" variant="outline" onClick={clearCall}>Fechar</Button>
                    )}
                </div>
            ) : connection ? (
                <VideoCallMedia
                    connection={connection}
                    popout={popout}
                    className={minimized ? 'hidden' : undefined}
                    elapsedSeconds={elapsedSeconds}
                    onHangUp={handleEnd}
                    variant={expanded ? 'room' : 'compact'}
                    onLeaveRoom={isLink ? (everConnected(call) ? leaveRoom : clearCall) : undefined}
                    onCopyLink={isLink && call.joinUrl ? copyLink : undefined}
                    invite={isLink && call.joinUrl ? { title: call.title || call.contactName || 'Chamada de vídeo', url: call.joinUrl, contactName: call.contactName, awaiting: !everConnected(call) } : undefined}
                    peerReconnecting={call.status === 'IDLE'}
                    onPeerLeftChange={setPeerLeft}
                    participants={{
                        waiting: waitingGuests,
                        busy: admitMutation.isPending || rejectMutation.isPending,
                        onAdmit: (guest) => decide(admitMutation, guest),
                        onReject: (guest) => decide(rejectMutation, guest),
                    }}
                    recording={{ agent: { videoCallId: call.id, api: RECORDING_API } }}
                    transcript={call.transcriptionEnabled
                        ? {
                            videoCallId: call.id, originAt: call.firstJoinedAt ?? null, title: call.title || call.contactName || 'Chamada de vídeo',
                            active: showTimer, api: TRANSCRIPT_API, panelPref, onPanelPrefChange: setPanelPref,
                        }
                        : undefined}
                >
                    <ConnectionQualityTracker tracker={qualityTracker} />
                </VideoCallMedia>
            ) : (
                <div className="flex flex-1 items-center justify-center p-4 text-sm text-foreground-muted">Conectando…</div>
            )}
        </div>
    );
}

export default VideoCallWindow;
